import { useState, useEffect, useRef } from 'react';

interface UseTypewriterOptions {
  /** List of strings to cycle through */
  words: string[];
  /** Typing speed in ms per character */
  typeSpeed?: number;
  /** Deleting speed in ms per character */
  deleteSpeed?: number;
  /** Pause duration after fully typed (ms) */
  pauseAfterType?: number;
  /** Pause duration after fully deleted (ms) */
  pauseAfterDelete?: number;
  /** Loop indefinitely */
  loop?: boolean;
}

interface UseTypewriterResult {
  text: string;
  isTyping: boolean;
  isDeleting: boolean;
  wordIndex: number;
}

export function useTypewriter({
  words,
  typeSpeed = 80,
  deleteSpeed = 45,
  pauseAfterType = 1800,
  pauseAfterDelete = 400,
  loop = true,
}: UseTypewriterOptions): UseTypewriterResult {
  const [text, setText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'pausing' | 'deleting' | 'waiting'>('typing');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const currentWord = words[wordIndex % words.length];

    const clear = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    if (phase === 'typing') {
      if (text.length < currentWord.length) {
        timeoutRef.current = setTimeout(() => {
          setText(currentWord.slice(0, text.length + 1));
        }, typeSpeed);
      } else {
        timeoutRef.current = setTimeout(() => setPhase('pausing'), pauseAfterType);
      }
    }

    if (phase === 'pausing') {
      timeoutRef.current = setTimeout(() => {
        if (loop || wordIndex < words.length - 1) {
          setPhase('deleting');
        }
      }, 0);
    }

    if (phase === 'deleting') {
      if (text.length > 0) {
        timeoutRef.current = setTimeout(() => {
          setText((prev) => prev.slice(0, -1));
        }, deleteSpeed);
      } else {
        timeoutRef.current = setTimeout(() => {
          setWordIndex((i) => (i + 1) % words.length);
          setPhase('waiting');
        }, pauseAfterDelete);
      }
    }

    if (phase === 'waiting') {
      timeoutRef.current = setTimeout(() => setPhase('typing'), 0);
    }

    return clear;
  }, [text, phase, wordIndex, words, typeSpeed, deleteSpeed, pauseAfterType, pauseAfterDelete, loop]);

  return {
    text,
    isTyping: phase === 'typing',
    isDeleting: phase === 'deleting',
    wordIndex: wordIndex % words.length,
  };
}